import { z } from 'zod';
import { HandicapSchema, PlayerIndexSchema, ScoringModeSchema } from "./events.js";
import { BallColorSchema, RedCountSchema } from "./balls.js";
import { normalizeCountryCode } from "./countries.js";
/**
 * Longest match the scoreboard offers. Even lengths are allowed: club and
 * league formats often play a fixed number of frames, so this is not
 * restricted to odd "best of" numbers.
 */
export const MIN_BEST_OF = 1;
export const MAX_BEST_OF = 19;
export const BestOfSchema = z.number().int().min(MIN_BEST_OF).max(MAX_BEST_OF);
/** A shot clock is not what this is: it caps the whole match. */
export const MIN_TIME_LIMIT_MINUTES = 1;
export const MAX_TIME_LIMIT_MINUTES = 600;
export const TimeLimitSchema = z
    .number()
    .int()
    .min(MIN_TIME_LIMIT_MINUTES)
    .max(MAX_TIME_LIMIT_MINUTES);
export const CreateMatchSchema = z.object({
    players: z.tuple([z.string().min(1), z.string().min(1)]),
    /**
     * Optional country per seat, used by stream overlays to draw flags.
     * `null` keeps guest/unknown seats valid.
     */
    countries: z
        .tuple([
        z
            .string()
            .trim()
            .transform((code) => normalizeCountryCode(code))
            .nullable(),
        z
            .string()
            .trim()
            .transform((code) => normalizeCountryCode(code))
            .nullable(),
    ])
        .default([null, null]),
    bestOf: BestOfSchema.default(1),
    redCount: RedCountSchema.optional(),
    /** AI is opt-in for every match; the control-bar switch enables it. */
    mode: ScoringModeSchema.default('manual'),
    /** Opt out of automatic streaming when opening a free manual match. */
    streaming: z.literal(false).optional(),
    /** Camera-backed solo practice; never a competitive result. */
    practice: z.literal(true).optional(),
    practicePots: z.array(z.union([BallColorSchema, z.literal('end')])).max(10_000).optional(),
    /**
     * Minutes the whole match may run for, or `null` for no limit. The clock
     * starts the moment the match is created, so this is deliberately not
     * changeable afterwards.
     */
    timeLimitMinutes: TimeLimitSchema.nullable().default(null),
    /**
     * Points one player gives the other at the start of every frame, or `null`
     * for an even match.
     */
    handicap: HandicapSchema.nullable().default(null),
    /**
     * Signed-in accounts for each seat, when there are any. A guest seat is
     * `null`, which is what keeps a walk-up game from needing a login.
     */
    playerIds: z.tuple([z.string().nullable(), z.string().nullable()]).optional(),
    /**
     * The club this match belongs to. Optional, because a walk-up game on a
     * laptop has no club and must not be made to invent one.
     *
     * When present the caller has to be a member, and it is what scopes the
     * match away from every other club on a shared server.
     */
    venueId: z.string().min(1).optional(),
}).refine(value => value.practice || value.practicePots === undefined, {
    message: 'practicePots requires practice mode',
});
/**
 * Ending a frame. Without `winner`, use the current score leader.
 * An explicit winner supports referee concessions, including tied scores.
 */
export const EndFrameSchema = z.object({
    winner: PlayerIndexSchema.optional(),
    clientId: z.string().min(1).optional(),
});
export const UndoSchema = z.object({
    clientId: z.string().min(1).optional(),
});
/** Ending a match early: a concession, walkover or retirement. */
export const EndMatchSchema = z.object({
    winner: PlayerIndexSchema,
    clientId: z.string().min(1).optional(),
});
/** Query parameter that hands a match to another device. */
export const MATCH_PARAM = 'm';
/**
 * Query parameter that opens a match as a phone remote control.
 *
 * Distinct from `MATCH_PARAM` on purpose. Both point at the same match and
 * both write to the same event log, but they ask for different screens: `m`
 * opens the whole scoreboard, which is meant to be read across a room, while
 * `r` opens a handful of big buttons meant to be held. A player who scans the
 * code at the table wants to add a red, not to carry a second scoreboard
 * around in their pocket.
 */
export const REMOTE_PARAM = 'r';
/**
 * Query parameter that carries a pairing code to a phone.
 *
 * A scoreboard shows a QR code; the phone that scans it registers or signs in
 * on its own screen and takes the seat. Nobody types a password on a shared
 * tablet, and a brand new player can join without the scoreboard ever needing
 * a camera.
 */
export const PAIR_PARAM = 'pair';
/**
 * Query parameter that opens the settings screen on a phone.
 *
 * The board can show its own settings — and asks for the password before it
 * changes anything — but typing a password on a tablet somebody else is
 * holding is unpleasant, and reading a nine-row ranking table across a room is
 * worse. So the settings screen also offers a QR code: scan it, sign in on
 * your own phone, and edit your account there. It carries no code and no
 * token, only the request for a screen — everything behind it still needs the
 * password, which is what makes the picture safe to leave on a shared board.
 */
export const SETTINGS_PARAM = 'settings';
/**
 * Query parameter that carries the scoreboard's chosen language to a phone.
 *
 * A phone that scans a code at the table belongs to the same room as the
 * board, and the club picked its language once on the home screen. Letting
 * the phone fall back to its own browser language hands a German player an
 * English sign-in form in the middle of a Chinese club night.
 */
export const LOCALE_PARAM = 'lang';
/**
 * How long a pairing code is good for. Generous, because on the other end of
 * it somebody is typing a name, a club and a password on a phone.
 */
export const PAIRING_TTL_SECONDS = 600;
export const ClaimPairingSchema = z.object({
    code: z.string().min(1).max(200),
});
export const WS_PATH = '/ws';
/** Header carrying the session token, as `Bearer <token>`. */
export const AUTH_HEADER = 'authorization';
export const API = {
    health: '/health',
    /** LAN addresses, so a scoreboard can build a QR link a phone can reach. */
    network: '/api/network',
    matches: '/api/matches',
    match: (id) => `/api/matches/${id}`,
    liveViewers: (id) => `/api/matches/${id}/live-viewers`,
    events: (id) => `/api/matches/${id}/events`,
    undo: (id) => `/api/matches/${id}/undo`,
    endFrame: (id) => `/api/matches/${id}/frames/end`,
    nextFrame: (id) => `/api/matches/${id}/frames/next`,
    endMatch: (id) => `/api/matches/${id}/end`,
    osd: (id) => `/api/matches/${id}/osd`,
    /** Verifies the tamper-evident chain over one match's event log. */
    integrity: (id) => `/api/matches/${id}/integrity`,
    register: '/api/auth/register',
    login: '/api/auth/login',
    logout: '/api/auth/logout',
    me: '/api/auth/me',
    /** Proves the password again before a settings change is allowed. */
    confirmPassword: '/api/auth/confirm',
    /** A scoreboard opens a pairing and shows it as a QR for a phone to take. */
    pair: '/api/auth/pair',
    /** The scoreboard polls this with its poll secret while it waits. */
    pairStatus: '/api/auth/pair/status',
    /** The phone attaches its freshly signed-in account to the pairing. */
    claimPair: '/api/auth/pair/claim',
    updateMe: '/api/players/me',
    deleteMe: '/api/players/me',
    avatar: '/api/players/me/avatar',
    myStats: '/api/players/me/stats',
    /** Where the signed-in player stands on every board, in every scope. */
    myRanks: '/api/players/me/ranks',
    playerSearch: '/api/players/search',
    player: (publicId) => `/api/players/${publicId}`,
    playerStats: (publicId) => `/api/players/${publicId}/stats`,
    leaderboard: '/api/leaderboard',
    challenges: '/api/practice/challenges',
    practiceSessions: '/api/practice/sessions',
    finishPractice: (id) => `/api/practice/sessions/${id}/finish`,
    practiceProgress: '/api/practice/me/progress',
};
//# sourceMappingURL=api.js.map